"use client";

import React from "react";
import { CircleCheck, PhoneCall, ShieldAlert, Clock, HardHat, ArrowRight, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const features = [{
  title: "Emergency Response",
  description: "24/7 rapid deployment to secure your property immediately.",
  icon: Clock
}, {
  title: "Temporary Protection",
  description: "Immediate tarping and board-up services to prevent further damage.",
  icon: HardHat
}, {
  title: "Detailed Inspection",
  description: "Comprehensive assessment of all structural and surface damage.",
  icon: CircleCheck
}, {
  title: "Insurance Coordination",
  description: "Seamless communication and documentation for your claims process.",
  icon: ShieldAlert
}, {
  title: "Quality Repairs",
  description: "Lasting restoration using premium materials and expert craftsmanship.",
  icon: CircleCheck
}];
const serviceAreas = [{
  name: "Montgomery County",
  path: "/ServiceAreasPage"
}, {
  name: "Howard County",
  path: "/ServiceAreasPage"
}, {
  name: "Frederick County",
  path: "/ServiceAreasPage"
}, {
  name: "Northern Virginia",
  path: "/ServiceAreasPage"
}, {
  name: "View All Service Areas",
  path: "/ServiceAreasPage"
}];
export default function SwiftStormDamageRepair() {
  const {
    ref: textRef,
    isVisible: isTextVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: imageRef,
    isVisible: isImageVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="swift-storm-damage-repair" className="bg-background py-16 md:py-24 overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          
          <div ref={textRef} className={`flex flex-col items-start transition-all duration-1000 ease-out ${isTextVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <Badge variant="secondary" className="mb-6 text-sm px-3 py-1">
              Rapid Response Team
            </Badge>
            
            <h2 className="font-default text-3xl md:text-4xl lg:text-5xl font-bold text-foreground leading-tight mb-6">Storm Damage Roof Repair Services for Maryland &amp; Northern Virginia&nbsp;</h2>

            <nav className="flex flex-wrap items-center gap-2 mb-8 text-sm text-muted-foreground">
              {serviceAreas.map((area, i) => <React.Fragment key={i}>
                  <Link to={area.path} className="hover:text-secondary transition-colors font-medium" data-index={i}>
                    {area.name}
                  </Link>
                  {i < serviceAreas.length - 1 && <ChevronRight className="w-4 h-4 opacity-50" />}
                </React.Fragment>)}
            </nav>
            
            <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
              We respond fast—usually within 1 hour—to secure your home and prevent further damage while you contact your insurance.
            </p>

            <div className="w-full space-y-6 mb-6">
              {features.map((feature, i) => <div key={i} data-index={i} className="flex items-start gap-4">
                  <div className="flex-shrink-0 mt-1 bg-secondary/10 p-2 rounded-md">
                    <feature.icon className="w-5 h-5 text-secondary" />
                  </div>
                  <div>
                    <h3 className="font-default font-semibold text-foreground text-base">
                      {feature.title}
                    </h3>
                    <p className="text-muted-foreground text-sm mt-1">
                      {feature.description}
                    </p>
                  </div>
                </div>)}
            </div>

            <p className="text-muted-foreground text-sm mb-10 font-medium border-l-2 border-secondary pl-4">
              Serving Montgomery County, Howard County, and Northern Virginia communities with 24/7 emergency response
            </p>

            <div className="mb-8 space-y-2">
              <p className="text-foreground font-semibold flex items-center gap-2">
                <CircleCheck className="w-5 h-5 text-secondary" />
                Insurance specialists guide your claim process
              </p>
              <p className="text-muted-foreground text-sm italic">
                We prioritize emergency calls - most homeowners hear from us within 1 hour.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 w-full sm:w-auto">
              <Button asChild size="lg" className="w-full sm:w-auto group">
                <Link to="/ContactPage">
                  Get Immediate Storm Assessment – We're here 24/7 for your peace of mind.
                  <ArrowRight className="ml-2 w-4 h-4 transition-transform group-hover:translate-x-1" />
                </Link>
              </Button>
              <Button asChild variant="secondary" size="lg" className="w-full sm:w-auto text-lg px-8 shadow-lg hover:scale-105 transition-transform">
                <Link to="/ContactPage">
                  <PhoneCall className="mr-2 w-5 h-5" />
                  Call Now
                </Link>
              </Button>
            </div>
          </div>

          <div ref={imageRef} className={`relative transition-all duration-1000 delay-200 ease-out ${isImageVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-8"}`}>
            <div className="relative rounded-2xl overflow-hidden shadow-2xl aspect-[4/5] md:aspect-square lg:aspect-[4/5]">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1660464/img/storm_damage_repair_hero.png" alt="Professional team performing emergency storm damage roof repair" className="object-cover w-full h-full" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1660464/img/storm_damage_repair_hero.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1660464/img/storm_damage_repair_hero.png-wvc-sizes" loading="lazy" />
              <div className="absolute inset-0 bg-gradient-to-t from-primary-900/80 via-primary-900/20 to-transparent" />
              
              <div className="absolute bottom-6 left-6 right-6">
                <Card className="bg-background/95 backdrop-blur-sm border-none shadow-lg">
                  <CardContent className="p-6 flex items-center gap-4">
                    <div className="bg-secondary rounded-lg p-3 shrink-0">
                      <ShieldAlert className="w-6 h-6 text-secondary-foreground" />
                    </div>
                    <div>
                      <p className="font-default font-bold text-foreground text-lg">Fully Licensed & Insured</p>
                      <p className="text-sm text-muted-foreground">Trusted local restoration experts</p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
            
            <div className="absolute -z-10 top-1/2 -translate-y-1/2 -right-12 w-64 h-64 bg-secondary/20 rounded-lg blur-3xl" />
          </div>

        </div>
      </div>
    </section>;
}